import { useCallback, useMemo, useState } from "react";
import { useDebouncedValue } from "~/common/hooks/useDebouncedValue";
import {
  DdqProdOpsQueueSortField,
  DdqProdOpsQueueSortOrder,
  DdqProdOpsWorkflowStatus,
  DdqProdOpsWorkflowType,
} from "~/graphql/supergraphEnums";

export const SEARCH_DEBOUNCE_MS = 300;

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export type DdqQueuePageSize = (typeof PAGE_SIZE_OPTIONS)[number];
const DEFAULT_PAGE_SIZE: DdqQueuePageSize = PAGE_SIZE_OPTIONS[0];

export const DEFAULT_SORT_BY = DdqProdOpsQueueSortField.DueAt;
export const DEFAULT_SORT_ORDER = DdqProdOpsQueueSortOrder.Asc;

// The queue excludes completed workflows by default -- also what the Status filter's Reset
// returns to, not ag-grid's own "everything checked" default.
export const DEFAULT_STATUSES: DdqProdOpsWorkflowStatus[] = Object.values(
  DdqProdOpsWorkflowStatus,
).filter((status) => status !== DdqProdOpsWorkflowStatus.Completed);

// The two statuses a summary card can isolate. `In progress`/`In review` have no card -- the
// Status column filter is the only way to isolate those (see DDQueue/labels.ts).
export const STATUS_CARD_VALUES = [
  DdqProdOpsWorkflowStatus.Pending,
  DdqProdOpsWorkflowStatus.OnHold,
] as const;

export const WORKFLOW_TYPE_CARD_VALUES = Object.values(DdqProdOpsWorkflowType);

export type DdqQueueFilterVariables = {
  search: string | undefined;
  workflowTypes: DdqProdOpsWorkflowType[] | undefined;
  statuses: DdqProdOpsWorkflowStatus[];
  sortBy: DdqProdOpsQueueSortField;
  sortOrder: DdqProdOpsQueueSortOrder;
  limit: DdqQueuePageSize;
  offset: number;
};

export type UseDdqQueueFiltersResult = {
  search: string;
  setSearch: (value: string) => void;
  columnWorkflowTypes: DdqProdOpsWorkflowType[] | null;
  setColumnWorkflowTypes: (values: DdqProdOpsWorkflowType[] | null) => void;
  columnStatuses: DdqProdOpsWorkflowStatus[] | null;
  setColumnStatuses: (values: DdqProdOpsWorkflowStatus[] | null) => void;
  cardWorkflowTypes: Set<DdqProdOpsWorkflowType>;
  toggleWorkflowTypeCard: (type: DdqProdOpsWorkflowType) => void;
  cardStatuses: Set<DdqProdOpsWorkflowStatus>;
  toggleStatusCard: (status: DdqProdOpsWorkflowStatus) => void;
  sortBy: DdqProdOpsQueueSortField;
  sortOrder: DdqProdOpsQueueSortOrder;
  setSort: (sortBy: DdqProdOpsQueueSortField, sortOrder: DdqProdOpsQueueSortOrder) => void;
  pageSize: DdqQueuePageSize;
  setPageSize: (pageSize: DdqQueuePageSize) => void;
  offset: number;
  setOffset: (offset: number) => void;
  reset: () => void;
  variables: DdqQueueFilterVariables;
};

// Single source of truth for the queue's search box, summary cards, column filters, sort and
// pagination. Card selection and column-filter selection are independent state, combined by
// intersection when both constrain the same group -- the two filter UIs are never visually
// synced against each other.
export const useDdqQueueFilters = (): UseDdqQueueFiltersResult => {
  const [search, setSearchRaw] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const [columnWorkflowTypes, setColumnWorkflowTypesRaw] = useState<
    DdqProdOpsWorkflowType[] | null
  >(null);
  const [columnStatuses, setColumnStatusesRaw] = useState<DdqProdOpsWorkflowStatus[] | null>(
    null,
  );

  const [cardWorkflowTypes, setCardWorkflowTypes] = useState<Set<DdqProdOpsWorkflowType>>(
    () => new Set(),
  );
  const [cardStatuses, setCardStatuses] = useState<Set<DdqProdOpsWorkflowStatus>>(
    () => new Set(),
  );

  const [sortBy, setSortByRaw] = useState<DdqProdOpsQueueSortField>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrderRaw] = useState<DdqProdOpsQueueSortOrder>(DEFAULT_SORT_ORDER);

  const [pageSize, setPageSizeRaw] = useState<DdqQueuePageSize>(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);

  const setSearch = useCallback((value: string) => {
    setSearchRaw(value);
    setOffset(0);
  }, []);

  const setColumnWorkflowTypes = useCallback((values: DdqProdOpsWorkflowType[] | null) => {
    setColumnWorkflowTypesRaw(values);
    setOffset(0);
  }, []);

  const setColumnStatuses = useCallback((values: DdqProdOpsWorkflowStatus[] | null) => {
    setColumnStatusesRaw(values);
    setOffset(0);
  }, []);

  const toggleWorkflowTypeCard = useCallback((type: DdqProdOpsWorkflowType) => {
    setCardWorkflowTypes((current) => {
      const next = new Set(current);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
    setOffset(0);
  }, []);

  const toggleStatusCard = useCallback((status: DdqProdOpsWorkflowStatus) => {
    setCardStatuses((current) => {
      const next = new Set(current);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
    setOffset(0);
  }, []);

  const setSort = useCallback(
    (nextSortBy: DdqProdOpsQueueSortField, nextSortOrder: DdqProdOpsQueueSortOrder) => {
      setSortByRaw(nextSortBy);
      setSortOrderRaw(nextSortOrder);
      setOffset(0);
    },
    [],
  );

  const setPageSize = useCallback((next: DdqQueuePageSize) => {
    setPageSizeRaw(next);
    setOffset(0);
  }, []);

  const reset = useCallback(() => {
    setSearchRaw("");
    setColumnWorkflowTypesRaw(null);
    setColumnStatusesRaw(null);
    setCardWorkflowTypes(new Set());
    setCardStatuses(new Set());
    setSortByRaw(DEFAULT_SORT_BY);
    setSortOrderRaw(DEFAULT_SORT_ORDER);
    setPageSizeRaw(DEFAULT_PAGE_SIZE);
    setOffset(0);
  }, []);

  const workflowTypes = useMemo(() => {
    const cards = cardWorkflowTypes.size > 0 ? Array.from(cardWorkflowTypes) : null;
    if (columnWorkflowTypes && cards) {
      return columnWorkflowTypes.filter((type) => cardWorkflowTypes.has(type));
    }
    return columnWorkflowTypes ?? cards ?? undefined;
  }, [columnWorkflowTypes, cardWorkflowTypes]);

  const statuses = useMemo(() => {
    // Completed can never be a real selection through the column filter -- ag-grid's Set
    // Filter Reset re-checks every box, so any subsequent uncheck would otherwise resubmit it.
    const base = (columnStatuses ?? DEFAULT_STATUSES).filter(
      (status) => status !== DdqProdOpsWorkflowStatus.Completed,
    );
    if (cardStatuses.size > 0) {
      return base.filter((status) => cardStatuses.has(status));
    }
    return base;
  }, [columnStatuses, cardStatuses]);

  const variables = useMemo<DdqQueueFilterVariables>(
    () => ({
      search: debouncedSearch || undefined,
      workflowTypes,
      statuses,
      sortBy,
      sortOrder,
      limit: pageSize,
      offset,
    }),
    [debouncedSearch, workflowTypes, statuses, sortBy, sortOrder, pageSize, offset],
  );

  return {
    search,
    setSearch,
    columnWorkflowTypes,
    setColumnWorkflowTypes,
    columnStatuses,
    setColumnStatuses,
    cardWorkflowTypes,
    toggleWorkflowTypeCard,
    cardStatuses,
    toggleStatusCard,
    sortBy,
    sortOrder,
    setSort,
    pageSize,
    setPageSize,
    offset,
    setOffset,
    reset,
    variables,
  };
};
